import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import useGuidelineMenus from "../../../hooks/useGuidelineMenus";
import styles from "./GuidelineOverviewApp.module.scss";
import PopularTopics from "./PopularTopics";
import useAppModernisation from "../../../hooks/useAppModernisation";
import { handleClickGlDevelopment } from "./resourceLinkHandler";
import classNames from "classnames";
import { isPregnancyAndBreastfeedingPage } from "../../../utils/isPregnancyAndBreastfeedingPage";
import { getGuidelineUpdatesDitamapPath } from "../../../utils/guidelineAbbreviations";
import { isValidBookmapContent } from "../../../utils/ditaUtils";
import { selectDitaContent } from "../../../components/Content/ContentSlice";
import { contentThunk } from "../../../components/Content/ContentThunk";
import useGuidelineDitaUpdates from "../../../hooks/useGuidelineDitaUpdates";

const generateSelfSearch = (pathname) => {
  const arraySearch = pathname
    .replace("/guidelines/", "")
    .replace("/app/nav/", "")
    .replace("Pregnancy_and_Breastfeeding", "quicklinks")
    .split("/");
  const [glPage, ...rest] = arraySearch;
  return { glPage, submenu: rest };
};

const getUpdatePath = (isAppMode, guidelineName) => {
  if (isAppMode) {
    return `/resources/${guidelineName}/updates`;
  } else {
    return `/guidelines/${guidelineName}/updates`;
  }
};

const GuidelineOverviewApp = () => {
  const { pathname } = useLocation();
  const guidelineObj = generateSelfSearch(pathname);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAppMode } = useAppModernisation();
  const content = useSelector(selectDitaContent);

  const { resources } = useGuidelineMenus(guidelineObj);

  // Check if this guideline has DITA updates
  const hasDitaUpdates = useGuidelineDitaUpdates(guidelineObj.glPage);

  const handleWhatsNewClick = () => {
    const guidelineName = guidelineObj.glPage;

    const ditamapPath = getGuidelineUpdatesDitamapPath(guidelineName);

    if (!ditamapPath) {
      // No abbreviation found, fallback to external PDF
      const item = filterResourceLink("whats_new");
      if (item?.isExternal) {
        window.open(item.msPath, "_blank", "noopener,noreferrer");
      }
      return;
    }

    const updateContent = content?.content?.find(
      (c) => c.ditafile === ditamapPath,
    );

    if (updateContent && isValidBookmapContent(updateContent.content)) {
      // DITA updates exist in cache, navigate to the new updates page
      navigate(getUpdatePath(isAppMode, guidelineName));
    } else {
      dispatch(contentThunk(ditamapPath))
        .then((result) => {
          if (isValidBookmapContent(result.payload)) {
            navigate(getUpdatePath(isAppMode, guidelineName));
          } else {
            // No DITA updates or fetch failed, fallback to external PDF
            const item = filterResourceLink("whats_new");
            if (item?.isExternal) {
              window.open(item.msPath, "_blank", "noopener,noreferrer");
            }
          }
        })
        .catch(() => {
          const item = filterResourceLink("whats_new");
          if (item?.isExternal) {
            window.open(item.msPath, "_blank", "noopener,noreferrer");
          }
        });
    }
  };

  const handleOnClick = (name) => {
    // Special handling for "whats_new" - check for DITA updates
    if (name === "whats_new") {
      handleWhatsNewClick();
      return;
    }

    const item = filterResourceLink(name);
    if (item?.isExternal) {
      window.open(item.msPath, "_blank", "noopener,noreferrer");
    } else {
      navigate(item.msPath);
    }
  };

  const filterResourceLink = (name) => {
    return resources.find((item) => {
      return item.key.toLowerCase() === name;
    });
  };

  const handleClickFaq = () => {
    if (isAppMode) {
      navigate("/resources/faq");
    } else {
      navigate("/faq");
    }
  };

  const hideForPbsPage = () => {
    return !isPregnancyAndBreastfeedingPage(pathname);
  };

  return (
    <div className={styles.guidelineOverviewWrapper}>
      <div className={styles.guidelineInformation}>
        <div className={classNames("size-18 semibold")}>
          Guidelines information
        </div>
        {hideForPbsPage() && (
          <div
            className={styles.linkItem}
            onClick={() => handleOnClick("whats_new")}
          >
            Updates
            {hasDitaUpdates !== null && (
              <i
                className={
                  hasDitaUpdates ? "tg-icons-LeftArrow" : "tg-icons-Open"
                }
              ></i>
            )}
          </div>
        )}
        <div
          className={styles.linkItem}
          onClick={() => handleOnClick("expert-groups", false)}
        >
          Expert group
          <i className="tg-icons-LeftArrow"></i>
        </div>
        <div
          className={styles.linkItem}
          onClick={() => handleOnClick("acknowledgements", false)}
        >
          Acknowledgements
          <i className="tg-icons-LeftArrow"></i>
        </div>
        {hideForPbsPage() && (
          <div
            className={styles.linkItem}
            onClick={() => handleOnClick("declaration_of_interest")}
          >
            Declaration of interest
            <i className="tg-icons-Open"></i>
          </div>
        )}
      </div>
      <div className={styles.supportLinks}>
        <div className={classNames("size-18 semibold")}>Support links</div>
        <div className={styles.linkItem} onClick={handleClickGlDevelopment}>
          Guideline development
          <i className="tg-icons-Open"></i>
        </div>

        <div className={styles.linkItem} onClick={handleClickFaq}>
          FAQs
          <i className="tg-icons-LeftArrow"></i>
        </div>
      </div>
      <PopularTopics glObj={guidelineObj} />
    </div>
  );
};

export default GuidelineOverviewApp;
